import { useEffect, useMemo, useState } from "react";
import {
  getAllRoutes,
  getAllRoutesByCompany,
  getChannels,
  getCustomerGroups,
  getListProduct,
} from "../../services";
import { Branch, Route } from "../../types/user.type";
import { CustomerChannel, CustomerGroup } from "../../types/customer.type";
import { ProductListItem } from "../../types/product.type";
import { useBranches } from "../SpecialOrder/useBranches";
import { CodeOption } from "./CodeMultiSelect";

/** The product catalogue is about 75 rows, so one page at the API's maximum covers it. */
const PRODUCT_LOOKUP_LIMIT = 100;

/** A route a Demand Factor can target: it is stored by `route_number`, so one is required. */
export type NumberedRoute = Route & { route_number: string };

export interface DemandFactorLookups {
  /** Head office and master only; a branch manager is pinned to its JWT branch. */
  branches: Branch[];
  routes: NumberedRoute[];
  groups: CustomerGroup[];
  channels: CustomerChannel[];
  groupNameByCode: Map<string, string>;
  channelNameByCode: Map<string, string>;
  productNameByCode: Map<string, string>;
  /** Both the form's target field and the preview's product filter pick from these. */
  productOptions: CodeOption[];
}

type ListPayload<T> = T[] | { rows?: T[] } | null | undefined;

interface LookupLists {
  routes: NumberedRoute[];
  groups: CustomerGroup[];
  channels: CustomerChannel[];
  products: ProductListItem[];
}

const EMPTY_LISTS: LookupLists = { routes: [], groups: [], channels: [], products: [] };

const hasRouteNumber = (route: Route): route is NumberedRoute =>
  Boolean(route.route_number);

/** Lookup endpoints answer either a bare array or a `{ rows }` page. */
async function loadRows<T>(
  request: () => Promise<{ data: { data: ListPayload<T> } }>,
): Promise<T[]> {
  try {
    const {
      data: { data },
    } = await request();
    return Array.isArray(data) ? data : data?.rows ?? [];
  } catch {
    // The axios interceptor has already reported the failure; an empty picker degrades to
    // "ทั้งหมด" and keeps the page usable.
    return [];
  }
}

const requestAllRoutes = () => getAllRoutes();
const requestOwnBranchRoutes = () => getAllRoutesByCompany();
const requestProducts = () => getListProduct(1, PRODUCT_LOOKUP_LIMIT);

const toNameMap = <T extends { code: string; name: string }>(items: T[]) =>
  new Map(items.map((item) => [item.code, item.name]));

/**
 * The code lists a Demand Factor targets, and their display names. Head office picks routes
 * across every branch (`/routes`); a branch manager only has its own (`/routes/by-company`).
 */
export function useDemandFactorLookups(canSelectBranch: boolean): DemandFactorLookups {
  const branches = useBranches(canSelectBranch);
  const [lists, setLists] = useState<LookupLists>(EMPTY_LISTS);

  useEffect(() => {
    let active = true;
    const loadLists = async () => {
      const [routes, groups, channels, products] = await Promise.all([
        loadRows<Route>(canSelectBranch ? requestAllRoutes : requestOwnBranchRoutes),
        loadRows<CustomerGroup>(getCustomerGroups),
        loadRows<CustomerChannel>(getChannels),
        loadRows<ProductListItem>(requestProducts),
      ]);
      if (active) {
        setLists({ routes: routes.filter(hasRouteNumber), groups, channels, products });
      }
    };
    loadLists();
    return () => {
      active = false;
    };
  }, [canSelectBranch]);

  return useMemo(
    () => ({
      branches,
      routes: lists.routes,
      groups: lists.groups,
      channels: lists.channels,
      groupNameByCode: toNameMap(lists.groups),
      channelNameByCode: toNameMap(lists.channels),
      productNameByCode: toNameMap(lists.products),
      productOptions: lists.products.map((product) => ({
        code: product.code,
        label: `${product.code} - ${product.name}`,
      })),
    }),
    [branches, lists],
  );
}
